import { PayloadAction, createSlice } from "@reduxjs/toolkit";

import { mapAPI } from "services/mapSlice";
import type {
  ExposedLimitInterpretation,
  ExposedLimitPerilCode,
} from "src/ts-types/ApiTypes";

const EXPOSED_LIMIT_INTERPRETATION_KEY = "exposedLimitInterpretation";
const PIN_COLOR_MODE_KEY = "portfolioPinColorMode";
const LAYER_TYPE_COLORS_KEY = "portfolioLayerTypeColors";

export type PortfolioMapMetric = "tiv" | ExposedLimitPerilCode;

/** A location's layer type: excess when only excess layers (attaching above zero) cover it, otherwise primary. */
export type PortfolioLayerType = "primary" | "excess";

// Pins follow "Map shows", or are coloured by the types of the layers covering each location.
export type PortfolioPinColorMode = "metric" | "layer_type";

export const DEFAULT_LAYER_TYPE_COLORS: Record<PortfolioLayerType, string> = {
  primary: "#16A34A",
  excess: "#EA580C",
};

const HEX_COLOR = /^#[0-9a-f]{6}$/i;

const loadLayerTypeColors = (): Record<PortfolioLayerType, string> => {
  let stored: unknown = null;
  try {
    stored = JSON.parse(localStorage.getItem(LAYER_TYPE_COLORS_KEY) ?? "null");
  } catch {
    // A hand-edited or corrupt value falls back to the defaults.
  }
  const colors = { ...DEFAULT_LAYER_TYPE_COLORS };
  if (stored && typeof stored === "object") {
    for (const layerType of Object.keys(colors) as PortfolioLayerType[]) {
      const color = (stored as Record<string, unknown>)[layerType];
      if (typeof color === "string" && HEX_COLOR.test(color)) {
        colors[layerType] = color;
      }
    }
  }
  return colors;
};

/** State that only Ping Portfolio maps use. Regular maps keep `isPortfolio` false and never read the rest. */
type PortfolioState = {
  // From the policy overview: the sovid is a Ping Portfolio.
  isPortfolio: boolean;
  /**
   * Deductible composition for exposed limits. A rarely changed default, set from the exposed
   * limits settings menu and remembered per browser.
   */
  exposedLimitInterpretation: ExposedLimitInterpretation;
  // What the map colours by: TIV, or one peril's exposed limit.
  mapMetric: PortfolioMapMetric;
  // County overlay (Overlays → Admin → County). At most one of it and the State overlay is shown.
  showCountyLayer: boolean;
  // Remembered per browser, with the colours the user picked for each layer type.
  pinColorMode: PortfolioPinColorMode;
  layerTypeColors: Record<PortfolioLayerType, string>;
};

const initialState: PortfolioState = {
  isPortfolio: false,
  exposedLimitInterpretation:
    localStorage.getItem(EXPOSED_LIMIT_INTERPRETATION_KEY) === "greater_of"
      ? "greater_of"
      : "additive",
  mapMetric: "tiv",
  showCountyLayer: false,
  pinColorMode:
    localStorage.getItem(PIN_COLOR_MODE_KEY) === "layer_type"
      ? "layer_type"
      : "metric",
  layerTypeColors: loadLayerTypeColors(),
};

const portfolioSlice = createSlice({
  name: "portfolio",
  initialState,
  reducers: {
    setExposedLimitInterpretation(
      state,
      action: PayloadAction<ExposedLimitInterpretation>,
    ) {
      state.exposedLimitInterpretation = action.payload;
      localStorage.setItem(EXPOSED_LIMIT_INTERPRETATION_KEY, action.payload);
    },
    setMapMetric(state, action: PayloadAction<PortfolioMapMetric>) {
      state.mapMetric = action.payload;
    },
    setShowCountyLayer(state, action: PayloadAction<boolean>) {
      state.showCountyLayer = action.payload;
    },
    setPinColorMode(state, action: PayloadAction<PortfolioPinColorMode>) {
      state.pinColorMode = action.payload;
      localStorage.setItem(PIN_COLOR_MODE_KEY, action.payload);
    },
    setLayerTypeColor(
      state,
      action: PayloadAction<{ layerType: PortfolioLayerType; color: string }>,
    ) {
      state.layerTypeColors[action.payload.layerType] = action.payload.color;
      localStorage.setItem(
        LAYER_TYPE_COLORS_KEY,
        JSON.stringify(state.layerTypeColors),
      );
    },
    resetLayerTypeColors(state) {
      state.layerTypeColors = { ...DEFAULT_LAYER_TYPE_COLORS };
      localStorage.removeItem(LAYER_TYPE_COLORS_KEY);
    },
  },
  extraReducers(builder) {
    builder.addMatcher(
      mapAPI.endpoints.getPolicyOverview.matchFulfilled,
      (state, action) => {
        state.isPortfolio = action.payload?.is_portfolio === true;
      },
    );
  },
});

export const {
  setExposedLimitInterpretation,
  setMapMetric,
  setShowCountyLayer,
  setPinColorMode,
  setLayerTypeColor,
  resetLayerTypeColors,
} = portfolioSlice.actions;

export default portfolioSlice.reducer;
